import { type Config } from 'tailwindcss';
import animatePlugin from 'tailwindcss-animate';
import radixPlugin from 'tailwindcss-radix';
import plugin from 'tailwindcss/plugin';

// Please refer to this link to see what tailwind styles exist already - e.g. fontWeight
// https://github.com/tailwindlabs/tailwindcss/blob/master/stubs/config.full.js
/* eslint-disable @typescript-eslint/no-var-requires */
export default {
  content: ['./index.html', './src/**/*.{js,jsx,ts,tsx}'],
  theme: {
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      background: 'var(--background)',
      primary: '#B8C4BA',
      secondary: {
        '15': '#D79C6E0F',
        '20': '#FDF9F6',
        '30': '#DEDAD7',
        '40': '#B8C9D3',
        '50': '#E6A370',
        '100': '#D79C6E',
        '200': '#BF8A62',
        '300': '#A87956',
        DEFAULT: '#D79C6E',
      },
      brand: {
        // via: https://uicolors.app/generate/6B8276
        '50': '#B8C4BA80',
        '100': '#e0e7e2',
        '200': '#c1cec7',
        '300': '#9aaea3',
        '400': '#6B8276', // DEFAULT
        '500': '#5a7265',
        '600': '#475a51',
        '700': '#3b4a43',
        '800': '#3b4a43',
        '900': '#2c3530',
        '950': '#161d1a',
        DEFAULT: '#6B8276',
      },
      error: {
        '50': '#FEF2F2',
        '200': '#FF0000',
        '500': '#b91c11',
        '600': '#7d0f0f',
        DEFAULT: '#FEF2F2',
      },
      warning: {
        '50': '#FFF7ED',
        '600': '#F97316',
        DEFAULT: '#FFF7ED',
      },
      review: {
        '50': '#FFF2CC',
        '600': '#FFC000',
        DEFAULT: '#FFF2CC',
      },
      success: {
        '50': '#F0FDF4',
        '600': '#00B050',
        DEFAULT: '#F0FDF4',
      },
      information: {
        '50': '#EFF6FF',
        '600': '#2563EB',
        DEFAULT: '#EFF6FF',
      },
      neutral: {
        '0': '#FFFFFF',
        '25': '#F1F5F9',
        '50': '#F9FAFB',
        '100': '#F3F4F6',
        '200': '#E5E7EB',
        '300': '#D1D5DB',
        '400': '#9CA3AF',
        '450': '#A09F9F',
        '500': '#6B7280',
        '600': '#4B5563',
        '700': '#374151',
        '725': '#4F4F4F',
        '750': '#474747',
        '800': '#1F2937',
        '900': '#111827',
        '950': '#030712',
        DEFAULT: '#F9FAFB',
      },
      phScale: {
        '1': '#DA0013',
        '2': '#F97F04',
        '3': '#FFC400',
        '4': '#FFED03',
        '5': '#BED94D',
        '6': '#91C556',
        '7': '#57C45F',
        '8': '#009A44',
        '9': '#3EBD92',
        '10': '#14CCC2',
        '11': '#4893CA',
        '12': '#2A52A9',
        '13': '#455199',
        '14': '#6349A0',
      },
      graph: {
        'bar-bg': '#81C9BE',
        'bar-border': '#43A1B5',
        'scatter-primary': '#8A7D80',
        'scatter-secondary': '#2F9F7A',
      },
      black: '#111827',
      white: '#FFFFFF',
      green: '#1B972A',
      yellow: '#F6D252',
      orange: '#F97316',
      red: '#E53904',
      destructive: '#b91c11',
      grey: '#ABABAB',
    },
    extend: {
      borderRadius: {
        lg: 'var(--radius)',
        md: 'calc(var(--radius) - 2px)',
        sm: 'calc(var(--radius) - 4px)',
      },
      colors: {
        sidebar: {
          DEFAULT: 'hsl(var(--sidebar-background))',
          foreground: 'hsl(var(--sidebar-foreground))',
          primary: 'hsl(var(--sidebar-primary))',
          'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
          accent: 'hsl(var(--sidebar-accent))',
          'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
          border: 'hsl(var(--sidebar-border))',
          ring: 'hsl(var(--sidebar-ring))',
        },
        soilPyramid: {
          Cl: '#D79C6E',
          SaCl: '#2F9F7A',
          SiCl: '#43A1B5',
          SaClLo: '#6B8276',
          ClLo: '#81C9BE',
          SiClLo: '#B8C4BA',
          LoSa: '#DEDAD7',
          Sa: '#4F4F4F',
          SaLo: '#B8C9D3',
          SaSiLo: '#A09F9F',
          SiLo: '#8A7D80',
        },
      },
      fontSize: {
        xxs: '11px',
        xxxs: '10px',
        xxxxs: '8px',
      },
      width: {
        '1/7': '14.2857%',
      },
    },
  },
  plugins: [
    radixPlugin,
    animatePlugin,
    plugin(({ addUtilities }) => {
      addUtilities({
        '.align-decimal-right': {
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        },
        '.sticky-table-header': {
          position: 'sticky',
          top: '0',
          backgroundColor: '#fff',
          zIndex: '1',
        },
      });
    }),
  ],
} satisfies Config;
